import { DateTime } from "luxon";
import { Schema, boolean, string } from "yup";

import { DEFAULT_HS_GRAD_YEAR_MIN, DynamicFieldsAnswerType, PROGRAM_REQUIRED_FIELD } from "@portal/app/_common/types/program";
import { FormField, FormFieldError } from "../ui-elements/Form";
import {
  FormInlineRadioGroupWithFormik,
  FormInlineRadioItem,
} from "../ui-elements/FormInlineRadio";
import { FormInputPhoneNumber, FormInputText, FormInputWithParse } from "../ui-elements/FormInputs";
import { FunnelSelectWithFormik, type FunnelSelectOption } from "../formFunnel/FunnelSelect";
import { ValidatedFormikField } from "../ui-elements/formHelpers";
import { EducationLevel } from "@portal/app/_common/types/customerDetail";
import { yupRules } from "@portal/app/_common/lib/yupValidationRules";
import { useEmailTypoWarning } from "../hooks/useEmailTypoWarning";
import { EmailTypoWarning } from "../ui-elements/EmailTypoWarning";
import {
  cleanAndCapitalizeName,
  cleanMaxLengthNumber,
} from "@portal/app/_common/lib/formatRenderers";
import { GoogleAddressAutocomplete } from "@portal/app/_components/ui-elements/GoogleAddressAutocomplete";
import { RequiredFields } from "@portal/app/_common/types/utils";
import { Address } from "@portal/app/_common/types/address";
import {
  TextFieldSkeleton,
  TypographySkeleton,
} from "@portal/app/_components/ui-elements/Mui/DreamMuiSkeleton";
import { createElement, useMemo } from "react";

export type DynamicField = (typeof PROGRAM_REQUIRED_FIELD)[keyof typeof PROGRAM_REQUIRED_FIELD];

export { DEFAULT_HS_GRAD_YEAR_MIN };

export type FormikDynamicFieldProps = {
  formikKey?: string;
  formikAddressKeys?: RequiredFields<Address, "street" | "street2" | "city" | "state" | "zip"> & {
    place: google.maps.places.PlaceResult;
  };
  formikFieldProps?: {
    disabled?: boolean;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  };
  label?: string | null;
  loading?: boolean;
  /** Minimum accepted HS graduation year. Defaults to 1970. */
  hsGradYearMin?: number | null;
  /** Maximum accepted HS graduation year. Defaults to current year. */
  hsGradYearMax?: number | null;
};
/**
 *
 * HOW TO ADD ANOTHER REQUIRED FIELD:
 * 1. Add the question to components/types/program.ts
 * 2. Create a FormField component and yup validator, add them both to the map below.
 * 3. Add the new field to `transformValuesToCustomerInfo`, `transformEditInitialValuesToFormValues`,
 * `getCustomerInfoForEligibilityForm`, and `UpdateAdditionalInfoValues` in CustomerEligibilityForm.tsx
 * 4. Add the new field to `CustomerDetailMap` and `CustomerUpdateInput` in customer.ts
 * 5. Add the new field to `input` (and its schema) and an if statement for the new field under the existing detail if statement in `editCustomer.ts`
 * 6. Add the new field to `getCustomerMapFromDetails` in customerHelpers.ts
 *
 */

const FormikIsUSCitizen = ({
  formikFieldProps,
  formikKey = "IS_US_CITIZEN",
  label = "Are you a U.S. citizen?",
}: FormikDynamicFieldProps) => {
  return (
    <FormField
      styleToWrapper={{ margin: 0 }}
      label={label === null ? undefined : label}
      withoutLabelWrap
    >
      <FormInlineRadioGroupWithFormik name={formikKey}>
        <FormInlineRadioItem value={true} disabled={formikFieldProps?.disabled}>
          Yes
        </FormInlineRadioItem>
        <FormInlineRadioItem value={false} disabled={formikFieldProps?.disabled}>
          No
        </FormInlineRadioItem>
      </FormInlineRadioGroupWithFormik>
      <FormFieldError name={formikKey} />
    </FormField>
  );
};

const FormikGraduatedHighSchoolIn = ({
  formikFieldProps,
  formikKey = "GRADUATED_HIGH_SCHOOL_IN",
  label = "When did you graduate high school?",
  hsGradYearMin,
  hsGradYearMax,
}: FormikDynamicFieldProps) => {
  const minYear = hsGradYearMin ?? DEFAULT_HS_GRAD_YEAR_MIN;
  const maxYear = hsGradYearMax ?? DateTime.now().year;
  const yearOptions: FunnelSelectOption[] = useMemo(
    () =>
      Array.from({ length: maxYear - minYear + 1 }, (_, i) => {
        const year = (maxYear - i).toString();
        return { label: year, value: year };
      }),
    [minYear, maxYear],
  );

  return (
    <FormField styleToWrapper={{ margin: 0 }} label={label === null ? undefined : label}>
      <div data-testid="high-school-graduation-year">
        <FunnelSelectWithFormik
          name={formikKey}
          options={yearOptions}
          placeholder="Select year"
          disabled={formikFieldProps?.disabled}
        />
      </div>
      <FormFieldError name={formikKey} />
    </FormField>
  );
};

const FormikHasRNLicense = ({
  formikFieldProps,
  formikKey = "HAS_RN_LICENSE",
  label = "Do you have your registered nurse (RN) license?",
}: FormikDynamicFieldProps) => (
  <FormField
    styleToWrapper={{ margin: 0 }}
    label={label === null ? undefined : label}
    withoutLabelWrap
  >
    <FormInlineRadioGroupWithFormik name={formikKey}>
      <FormInlineRadioItem value={true} disabled={formikFieldProps?.disabled}>
        Yes
      </FormInlineRadioItem>
      <FormInlineRadioItem value={false} disabled={formikFieldProps?.disabled}>
        No, or currently obtaining
      </FormInlineRadioItem>
    </FormInlineRadioGroupWithFormik>
    <FormFieldError name={formikKey} />
  </FormField>
);

const FormikHasMilitaryAffiliation = ({
  formikFieldProps,
  formikKey = "HAS_MILITARY_AFFILIATION",
  label = "Do you have any military affiliation?",
}: FormikDynamicFieldProps) => (
  <FormField
    styleToWrapper={{ margin: 0 }}
    label={label === null ? undefined : label}
    withoutLabelWrap
  >
    <FormInlineRadioGroupWithFormik name={formikKey}>
      <FormInlineRadioItem value={true} disabled={formikFieldProps?.disabled}>
        Yes
      </FormInlineRadioItem>
      <FormInlineRadioItem value={false} disabled={formikFieldProps?.disabled}>
        No
      </FormInlineRadioItem>
    </FormInlineRadioGroupWithFormik>
    <FormFieldError name={formikKey} />
  </FormField>
);

const FormikEmail = ({
  formikFieldProps,
  formikKey = "EMAIL",
  label = "Email address",
  loading,
}: FormikDynamicFieldProps) => {
  const { onBlur, isMisspelled, warningBorderStyle } = useEmailTypoWarning({ fieldName: formikKey });
  return (
    <FormField styleToWrapper={{ margin: 0 }} label={label === null ? undefined : label}>
      {loading ? (
        <TextFieldSkeleton />
      ) : (
        <ValidatedFormikField
          as={FormInputText}
          name={formikKey}
          type="email"
          label="EMAIL"
          autoComplete="email"
          placeholder="email@example.com"
          disabled={formikFieldProps?.disabled}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            formikFieldProps?.onBlur?.(event);
            onBlur(event);
          }}
          style={warningBorderStyle}
        />
      )}
      {isMisspelled ? (
        <EmailTypoWarning />
      ) : (
        <FormFieldError name={formikKey} />
      )}
    </FormField>
  );
};

const FormikNameFirst = ({
  formikFieldProps,
  formikKey = "NAME_FIRST",
  label = "First name",
  loading,
}: FormikDynamicFieldProps) => (
  <FormField styleToWrapper={{ margin: 0 }} label={label === null ? undefined : label}>
    {loading ? (
      <TextFieldSkeleton />
    ) : (
      <ValidatedFormikField
        as={FormInputText}
        $inputAs={FormInputWithParse}
        $fromDisplay={cleanAndCapitalizeName}
        $toDisplay={cleanAndCapitalizeName}
        name={formikKey}
        autoComplete="given-name"
        placeholder="First name"
        disabled={formikFieldProps?.disabled}
        onBlur={formikFieldProps?.onBlur}
      />
    )}
    <FormFieldError name={formikKey} />
  </FormField>
);

const FormikNameLast = ({
  formikFieldProps,
  formikKey = "NAME_LAST",
  label = "Last name",
  loading,
}: FormikDynamicFieldProps) => (
  <FormField styleToWrapper={{ margin: 0 }} label={label === null ? undefined : label}>
    {loading ? (
      <TextFieldSkeleton />
    ) : (
      <ValidatedFormikField
        as={FormInputText}
        name={formikKey}
        autoComplete="family-name"
        placeholder="Last name"
        disabled={formikFieldProps?.disabled}
        onBlur={formikFieldProps?.onBlur}
      />
    )}
    <FormFieldError name={formikKey} />
  </FormField>
);

const FormikPhone = ({
  formikFieldProps,
  formikKey = "PHONE",
  label = "Cell phone number",
  loading,
}: FormikDynamicFieldProps) => (
  <FormField styleToWrapper={{ margin: 0 }} label={label === null ? undefined : label}>
    {loading ? (
      <TextFieldSkeleton />
    ) : (
      <ValidatedFormikField
        as={FormInputPhoneNumber}
        name={formikKey}
        disabled={formikFieldProps?.disabled}
        onKeyUp={formikFieldProps?.onKeyUp}
        onBlur={formikFieldProps?.onBlur}
      />
    )}
    <FormFieldError name={formikKey} />
  </FormField>
);

const FormikAddressFull = ({
  formikKey = "ADDRESS_FULL",
  label = "Home address",
  loading,
}: FormikDynamicFieldProps) => (
  <FormField styleToWrapper={{ margin: 0 }} label={label === null ? undefined : label}>
    {loading ? (
      <TypographySkeleton sx={{ height: "11.5rem" }} />
    ) : (
      <GoogleAddressAutocomplete requireStreetNumber fieldNamespace={formikKey} />
    )}
    <FormFieldError name={formikKey} />
  </FormField>
);

const FormikAddressZip = ({
  formikFieldProps,
  formikKey = "ADDRESS_ZIP",
  label = "ZIP code",
  loading,
}: FormikDynamicFieldProps) => (
  <FormField styleToWrapper={{ margin: 0 }} label={label === null ? undefined : label}>
    {loading ? (
      <TextFieldSkeleton />
    ) : (
      <ValidatedFormikField
        as={FormInputText}
        $inputAs={FormInputWithParse}
        $fromDisplay={cleanMaxLengthNumber(5)}
        $toDisplay={cleanMaxLengthNumber(5)}
        name={formikKey}
        maxLength={5}
        autoComplete="postal-code"
        inputMode="numeric"
        placeholder="54321"
        submitOnEnter
        disabled={formikFieldProps?.disabled}
        data-testid="zip"
        onBlur={formikFieldProps?.onBlur}
      />
    )}
    <FormFieldError name={formikKey} />
  </FormField>
);

const dynamicFieldToComponent: {
  [key in keyof DynamicFieldsAnswerType]: React.FC<FormikDynamicFieldProps>;
} = {
  IS_US_CITIZEN: FormikIsUSCitizen,
  GRADUATED_HIGH_SCHOOL_IN: FormikGraduatedHighSchoolIn,
  HAS_RN_LICENSE: FormikHasRNLicense,
  HAS_MILITARY_AFFILIATION: FormikHasMilitaryAffiliation,
  EMAIL: FormikEmail,
  NAME_FIRST: FormikNameFirst,
  NAME_LAST: FormikNameLast,
  PHONE: FormikPhone,
  ADDRESS_FULL: FormikAddressFull,
  ADDRESS_ZIP: FormikAddressZip,
};

export const educationLevelKey = "educationLevel";
const dynamicFieldToYupFilter: {
  [key in keyof DynamicFieldsAnswerType]: Schema;
} = {
  GRADUATED_HIGH_SCHOOL_IN: string().when(educationLevelKey, {
    is: (value: EducationLevel | undefined) =>
      value !== "CURRENT_HIGH_SCHOOL" && value !== "NO_HS_DIPLOMA_GED",
    then: (schema) =>
      schema.required("Year of graduation is required"),
    otherwise: (schema) => schema,
  }),
  IS_US_CITIZEN: boolean().required("Citizenship status is required"),
  HAS_RN_LICENSE: boolean()
    .required("A registered nurse license is required")
    .isTrue("A registered nurse license is required"),
  HAS_MILITARY_AFFILIATION: boolean(),
  EMAIL: yupRules.required.email,
  NAME_FIRST: yupRules.required.nameFirst,
  NAME_LAST: yupRules.required.nameLast,
  PHONE: yupRules.required.phone,
  ADDRESS_FULL: yupRules.required.address,
  ADDRESS_ZIP: yupRules.required.zip,
};

export const FormikDynamicField = ({
  questionKey,
  ...params
}: FormikDynamicFieldProps & {
  questionKey: keyof DynamicFieldsAnswerType;
}) => createElement(dynamicFieldToComponent[questionKey], params);

// Will return Yup filters for any valid custom question keys.
export const getYupFiltersForDynamicFields = (
  dynamicFieldKeys: DynamicField[]
): Record<DynamicField, Schema> =>
  Object.fromEntries(
    dynamicFieldKeys
      .map((dynamicFieldKey) =>
        dynamicFieldToYupFilter[dynamicFieldKey]
          ? [dynamicFieldKey, dynamicFieldToYupFilter[dynamicFieldKey]]
          : undefined
      )
      .filter((entry): entry is [DynamicField, Schema] => !!entry)
  ) as Record<DynamicField, Schema>;

